import { formatTimeAgo } from "@redotech/util/date-format";
import { memo } from "react";

interface DateDisplayProps {
  /** The date to display and format (ISO string or Date) */
  date: string | Date | undefined;
  /** Additional CSS classes */
  className?: string;
}

/**
 * A component that displays dates with smart formatting:
 * - If today: shows time (e.g., "2:30 PM") + relative time (e.g., "2h ago")
 * - If not today: shows day + time (e.g., "Yesterday 2:30 PM" or "Jan 15, 2:30 PM")
 */
export const SmartDateDisplay = memo(function SmartDateDisplay({
  date,
  className,
}: DateDisplayProps) {
  if (!date) {
    return (
      <span className={className} data-web="SmartDateDisplay">
        —
      </span>
    );
  }

  const dateObject = typeof date === "string" ? new Date(date) : date;
  const now = new Date();
  const isToday = dateObject.toDateString() === now.toDateString();

  if (isToday) {
    // For today: show time + relative time
    const timeString = dateObject.toLocaleString("en-US", {
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    });
    const relativeTime = formatTimeAgo(dateObject.toISOString());
    // If formatTimeAgo returns a time format (like "11:30 AM"), calculate relative time manually
    let displayRelativeTime = relativeTime;
    if (relativeTime.match(/\d{1,2}:\d{2}\s?(AM|PM)/i)) {
      // Calculate minutes difference manually for same-day times
      const minutesDiff = Math.floor(
        (now.getTime() - dateObject.getTime()) / (1000 * 60),
      );
      if (minutesDiff < 1) {
        displayRelativeTime = "Now";
      } else if (minutesDiff < 60) {
        displayRelativeTime = `${minutesDiff}m ago`;
      } else {
        const hoursDiff = Math.floor(minutesDiff / 60);
        displayRelativeTime = `${hoursDiff}h ago`;
      }
    } else if (relativeTime.match(/^\d+[mh]$/)) {
      // Add "ago" if it's just minutes/hours format (like "2m" or "3h")
      displayRelativeTime = `${relativeTime} ago`;
    }
    return (
      <span className={className} data-web="SmartDateDisplay">
        {timeString}{" "}
        <span style={{ opacity: 0.6, marginLeft: "0.5em" }}>
          ({displayRelativeTime})
        </span>
      </span>
    );
  } else {
    // For other days: show day + time
    const dayString = dateObject.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
    });
    const timeString = dateObject.toLocaleString("en-US", {
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    });
    return (
      <span className={className} data-web="SmartDateDisplay">
        {dayString}{" "}
        <span style={{ opacity: 0.6, marginLeft: "0.5em" }}>{timeString}</span>
      </span>
    );
  }
});
